import type { PersonRecord } from '@/data/schemas';
import type { IconName } from '@/components/ui/icon';

type PersonnelScope = 'company' | 'site';

function normalizeSiteId(siteId?: string | null): string | undefined {
  if (typeof siteId !== 'string') return undefined;
  const trimmed = siteId.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolvePersonnelScope(person?: Partial<Pick<PersonRecord, 'scope'>>): PersonnelScope {
  return person?.scope ?? 'company';
}

export function applyPersonnelScopeDefaults(
  personnel?: Array<Partial<PersonRecord>>
): PersonRecord[] {
  if (!personnel || personnel.length === 0) {
    return [];
  }

  return personnel.map((person) => {
    const scope = resolvePersonnelScope(person);
    const siteId =
      scope === 'site' ? normalizeSiteId((person as Partial<PersonRecord>).siteId) : undefined;
    return {
      ...person,
      scope,
      siteId,
    } as PersonRecord;
  });
}

export function normalizePersonnelInput<
  T extends Partial<Omit<PersonRecord, 'id' | 'createdAt' | 'updatedAt'>>,
>(input: T): T & { scope: PersonnelScope; siteId?: string } {
  const scope = resolvePersonnelScope(input);
  const siteId = scope === 'site' ? normalizeSiteId(input.siteId) : undefined;

  if (scope === 'site' && !siteId) {
    throw new Error('Personnel scoped to a site requires a valid siteId reference');
  }

  return {
    ...input,
    scope,
    siteId,
  };
}

export function filterPersonnelForSite(
  personnel: PersonRecord[] | undefined,
  siteId?: string | null
): PersonRecord[] {
  if (!personnel || personnel.length === 0) {
    return [];
  }

  const normalizedSiteId = normalizeSiteId(siteId);

  return personnel.filter((person) => {
    const scope = resolvePersonnelScope(person);
    if (scope === 'company') {
      return true;
    }
    if (!normalizedSiteId) {
      return false;
    }
    return normalizeSiteId(person.siteId) === normalizedSiteId;
  });
}

export function groupPersonnelByScope(
  personnel: PersonRecord[],
  sites: Array<{ id: string; name: string }>
): {
  company: PersonRecord[];
  siteGroups: Array<{
    siteId: string;
    siteName: string;
    personnel: PersonRecord[];
  }>;
  unassigned: PersonRecord[];
} {
  const normalizedPersonnel = applyPersonnelScopeDefaults(personnel);
  const siteNameMap = new Map(sites.map((site) => [site.id, site.name]));
  const company: PersonRecord[] = [];
  const siteAccumulator = new Map<string, PersonRecord[]>();
  const unassigned: PersonRecord[] = [];

  for (const person of normalizedPersonnel) {
    const scope = resolvePersonnelScope(person);
    if (scope === 'company') {
      company.push(person);
      continue;
    }

    const siteId = normalizeSiteId(person.siteId);
    if (!siteId) {
      unassigned.push({ ...person, scope: 'site', siteId: undefined });
      continue;
    }

    const bucket = siteAccumulator.get(siteId);
    if (bucket) {
      bucket.push({ ...person, siteId });
    } else {
      siteAccumulator.set(siteId, [{ ...person, siteId }]);
    }
  }

  const siteGroups = Array.from(siteAccumulator.entries())
    .map(([siteId, people]) => ({
      siteId,
      siteName: siteNameMap.get(siteId) ?? 'Stabilimento non trovato',
      personnel: people,
    }))
    .sort((a, b) => a.siteName.localeCompare(b.siteName, 'it'));

  return {
    company,
    siteGroups,
    unassigned,
  };
}

export function sortPersonnelByPriority(personnel: PersonRecord[]): PersonRecord[] {
  return [...personnel].sort((a, b) => {
    if (a.reportRecipient !== b.reportRecipient) {
      return a.reportRecipient ? -1 : 1;
    }
    if (a.alarmContact !== b.alarmContact) {
      return a.alarmContact ? -1 : 1;
    }
    const lastNameComparison = a.lastName.localeCompare(b.lastName, 'it');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }
    return a.firstName.localeCompare(b.firstName, 'it');
  });
}

export function buildPersonnelBadges(person: PersonRecord): Array<{
  key: string;
  label: string;
  tone: 'info' | 'warning' | 'success';
  icon: IconName;
}> {
  const badges: Array<{
    key: string;
    label: string;
    tone: 'info' | 'warning' | 'success';
    icon: IconName;
  }> = [];

  if (person.reportRecipient) {
    badges.push({
      key: 'reports',
      label: 'Verbali',
      tone: 'info',
      icon: 'mail',
    });
  }

  if (person.alarmContact) {
    badges.push({
      key: 'alarms',
      label: 'Allarmi',
      tone: 'warning',
      icon: 'notifications',
    });
  }

  if (person.reperibile) {
    badges.push({
      key: 'reperibile',
      label: 'Reperibile',
      tone: 'warning',
      icon: 'phone_enabled',
    });
  }

  if (person.licensed) {
    badges.push({
      key: 'license',
      label: 'Patentino',
      tone: 'success',
      icon: 'badge',
    });
  }

  return badges;
}
